import type { Notification } from './types';

/**
 * Where a notification's "open this" button goes.
 *
 * The Figma panel ends every message with an action — "View Report", "View
 * Portfolio". Its mock could promise anything; this has to resolve against
 * routes that exist, from the only linkage the API gives us.
 *
 * THE ONLY LINKAGE IS THE ID. `/v1/notifications` returns six strings and none
 * of them is an entity reference (`types.ts` says so, and it is worth repeating
 * here because this file looks like the place someone would add one). What it
 * does return is a composite id built in `notifications/service.py` as
 * `prefix:uuid` — `client:9a3f…`, `strat:71b2…` — so the prefix names the kind
 * and the tail is the row.
 *
 * ONE OF THE EIGHT KINDS RESOLVES EXACTLY: `client:` carries `Client.id`,
 * which is what `/clients/$clientId` and `getClient` both take (checked
 * against `_clients`, which selects that column directly). The other seven
 * carry an id no route accepts:
 *
 *   - `strat:` LOOKS exact and is not. `_strategies` sends `f"strat:{s.id}"`,
 *     and `Strategy.id` is the primary key of one VERSION row, while the
 *     report route and `getStrategy` are both keyed by `Strategy.strategy_id`
 *     — the logical strategy across versions. The two are different columns on
 *     the same table, so the wrong one type-checks, builds a plausible URL, and
 *     404s at the far end.
 *   - `bt:` is a backtest id; the report route is keyed by STRATEGY.
 *   - `lm:` is a live-model id, and `/strategies/$strategyId/live/$liveModelId`
 *     needs both — the strategy half is not in the notification.
 *   - `acct:` is a brokerage account id and `thread:` a conversation id;
 *     neither has a standalone route, both live inside a client page we cannot
 *     name.
 *   - `onboarding:` is a session id, and the account-opening route is keyed by
 *     client.
 *   - `user:` is a user id, and there is no per-member page at all.
 *
 * For those, the button goes to the SECTION and says so. A button labelled
 * "Open strategy" that lands on a list is worse than one labelled "Go to
 * Strategies" that lands on the same list: the first one looks broken, the
 * second one is just modest. Nothing here ever navigates to a route whose
 * params it had to invent.
 *
 * If a kind ever needs to land exactly, the fix is in `service.py` — give the
 * note the id the route wants — not a lookup here.
 */
export type Destination =
  | { to: '/clients/$clientId'; params: { clientId: string }; labelKey: string }
  | { to: '/clients'; labelKey: string }
  | { to: '/strategies'; labelKey: string }
  | { to: '/team'; labelKey: string };

/**
 * Split `prefix:tail` once, from the left.
 *
 * Once, because `onboarding:<session>:<index>` has two colons and the tail of
 * that one is not an id at all. Splitting on every colon would hand
 * `destinationFor` the string "9" as a session id.
 */
function split(id: string): { prefix: string; tail: string } {
  const at = id.indexOf(':');
  if (at === -1) return { prefix: id, tail: '' };
  return { prefix: id.slice(0, at), tail: id.slice(at + 1) };
}

/** The button for this notification, or null when there is nowhere useful. */
export function destinationFor(
  notification: Pick<Notification, 'id'>,
): Destination | null {
  const { prefix, tail } = split(notification.id);

  switch (prefix) {
    // Exact.
    case 'client':
      // An empty tail would build `/clients/` and render the detail page
      // against no client, so it falls through to the list instead.
      return tail
        ? {
            to: '/clients/$clientId',
            params: { clientId: tail },
            labelKey: 'header.notifications.open_client',
          }
        : { to: '/clients', labelKey: 'header.notifications.go_clients' };
    // The section, honestly labelled.
    case 'strat':
    case 'bt':
    case 'lm':
      return {
        to: '/strategies',
        labelKey: 'header.notifications.go_strategies',
      };
    case 'acct':
    case 'thread':
    case 'onboarding':
      return { to: '/clients', labelKey: 'header.notifications.go_clients' };
    case 'user':
      return { to: '/team', labelKey: 'header.notifications.go_team' };

    // A prefix minted server-side after this file was written. No button is
    // the right answer — guessing a section would send someone somewhere
    // unrelated and tell them it was the point.
    default:
      return null;
  }
}
